<template>
  <div class="navbar">
    <v-app-bar
      v-if="$auth.isAuthenticated && $vuetify.breakpoint.mdAndDown"
      color="white"
      clipped-left
      app
    >
      <template v-if="$vuetify.breakpoint.mdAndDown">
        <v-img
          alt="Firmen Logo"
          class="shrink"
          :src="require('@/../public/img/logo.png')"
          width="140"
          min-height="16"
          contain
          @click="goHome"
        />

        <v-spacer />
        <div class="d-flex align-center justify-center">
          <v-btn
            v-if="isEntryPlan"
            small
            :to="{ name: 'Settings', params: { section: 'contract' }, query: { showUpgrade: '1' } }"
            :style="{
              'background-color': isPlatformMarkero ?
                '#FFF5EA'
                : $vuetify.theme.themes.light.primary,
              'color': isPlatformMarkero ? $vuetify.theme.themes.light.primary : '#FFF',
            }"
            class="upgrade-button"
            @click="$tracking.event('AppBar', 'Clicked', 'Upgrade', 'Toad')"
          >
            <v-icon class="mr-2">
              mdi-one-up
            </v-icon>
            {{ $t('navigation.usage-counter.upgrade') }}
          </v-btn>
          <v-list-item
            v-if="news"
            :id="news.trackingLabel?.toLowerCase()"
            :key="news.to"
            :to="news.to"
            :href="news.href"
            :target="news.target"
            :rel="news.rel"
            @click="handleItemClick(news.trackingLabel)"
          >
            <v-list-item-icon class="news-icon">
              <span
                v-if="unreadNewsCount > 0"
                class="unread-messages-count"
              >
                {{ unreadNewsCount }}
              </span>
              <v-icon>
                {{ news.icon }}
              </v-icon>
            </v-list-item-icon>
          </v-list-item>
        </div>

        <v-app-bar-nav-icon
          v-if="$auth.isAuthenticated"
          color="primary"
          @click.stop="showDrawer = !showDrawer"
        />
      </template>
    </v-app-bar>

    <v-navigation-drawer
      v-if="$auth.isAuthenticated"
      ref="drawer"
      v-model="showDrawer"
      class="px-2 pb-3 navigation-drawer-height"
      clipped
      app
      mini-variant-width="60"
      :width="270"
      :right="$vuetify.breakpoint.mdAndDown"
      :expand-on-hover="isDrawerCollapsible"
      @mouseenter="updateDrawerState"
      @mouseleave="updateDrawerState"
      @transitionend="updateDrawerState"
    >
      <!-- Company Logo -->
      <template v-slot:prepend>
        <v-list-item
          id="company-logo"
          to="/dashboard"
          exact
          two-line
          link
          class="px-2"
          @click="trackClick('Logo')"
        >
          <v-list-item-avatar tile>
            <v-img
              :alt="$t('navigation.logo.alt-texts.0')"
              :src="require('@/../public/img/logo-icon.png')"
              @click="goHome"
            />
          </v-list-item-avatar>

          <v-list-item-content>
            <v-list-item-title>
              <v-img
                :alt="$t('navigation.logo.alt-texts.1')"
                :src="require('@/../public/img/logo-name.png')"
                @click="goHome"
              />
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </template>

      <!-- User Section -->
      <FeatureFlag
        :feature-name="featureNames.USAGE_COUNTER"
        @feature-change="onUsageCounterFeatureChange"
      >
        <v-divider />
        <v-list-item class="px-0">
          <v-menu
            style="z-index: 10000"
            offset-y
          >
            <template v-slot:activator="{ attrs, on }">
              <div
                class="d-flex align-center w-100 cursor-pointer user-section"
                v-bind="attrs"
                v-on="on"
              >
                <v-list-item-content
                  class="py-0"
                  :class="{'pl-4': isDrawerOpen}"
                >
                  <div class="d-flex align-center w-100">
                    <v-list-item-avatar
                      tile
                      class="portrait-wrapper"
                    >
                      <v-img
                        v-if="$auth.user.picture"
                        alt="User Avatar"
                        class="portrait"
                        :src="$auth.user.picture"
                      />
                    </v-list-item-avatar>
                    <div
                      v-show="isDrawerOpen"
                      class="d-flex flex-column flex-grow-1 user-details"
                    >
                      <div class="pl-0">
                        <v-list-item-title
                          v-if="$auth.user.given_name && $auth.user.family_name"
                          class="truncate mb-0"
                          :title="$auth.user.given_name + ' ' + $auth.user.family_name"
                        >
                          {{ $auth.user.given_name + ' ' + $auth.user.family_name }}
                        </v-list-item-title>
                        <v-list-item-subtitle
                          v-if="company"
                          class="text-caption grey--text truncate pl-0"
                          :title="company?.name"
                        >
                          {{ company?.name }}
                        </v-list-item-subtitle>
                      </div>
                      <v-spacer />
                    </div>

                    <v-btn
                      v-if="isDrawerOpen"
                      icon
                      medium
                      class="menu-button"
                      :ripple="false"
                    >
                      <v-icon>mdi-dots-vertical</v-icon>
                    </v-btn>
                  </div>
                </v-list-item-content>
              </div>
            </template>

            <v-list style="z-index: 10000">
              <v-list-item
                to="/settings/profile"
                dense
                exact
                link
                @click="trackClick('Profile')"
              >
                <v-list-item-icon><v-icon>mdi-account</v-icon></v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>{{ $t('navigation.labels.profile') }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item
                dense
                @click="logout"
              >
                <v-list-item-icon><v-icon>mdi-logout</v-icon></v-list-item-icon>
                <v-list-item-title>{{ $t('navigation.labels.logout') }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </v-list-item>
        <v-divider />
      </FeatureFlag>

      <div
        class="navigation-container"
      >
        <!-- Primary Navigation -->
        <v-list
          class="mt-0 primary-navigation"
        >
          <v-list-item
            v-for="item in navItems"
            :key="item.to"
            :to="item.to"
            class="rounded-lg"
            active-class="rounded-lg grey lighten-3 font-weight-bold"
            color="primary"
            @click="handleItemClick(item.trackingLabel)"
          >
            <v-list-item-icon>
              <v-badge
                v-if="item.badge && item.badge.condition({showRegeneration})"
                bordered
                bottom
                :color="item.badge.color"
                dot
                offset-x="10"
                offset-y="10"
              >
                <v-icon>{{ item.icon }}</v-icon>
              </v-badge>
              <v-badge
                v-else-if="item.isNew && showIsNew"
                content="NEW"
                color="orange"
                offset-x="10"
                offset-y="10"
              >
                <v-icon>{{ item.icon }}</v-icon>
              </v-badge>
              <v-icon v-else>
                {{ item.icon }}
              </v-icon>
            </v-list-item-icon>

            <v-list-item-content>
              <v-list-item-title>
                {{ $t(item.label) }}
                <v-badge
                  v-if="item.isBeta"
                  content="BETA"
                  color="orange"
                  class="ml-2"
                />
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list>

        <v-divider />

        <!-- Secondary Navigation -->
        <v-list
          dense
          class="bottom-navigation"
        >
          <v-list-item
            v-for="item in filteredNavItemsBottimmo"
            :id="item.trackingLabel.toLowerCase()"
            :key="item.to"
            :to="item.to"
            :href="item.href"
            :target="item.target"
            :rel="item.rel"
            @click="handleItemClick(item.trackingLabel)"
          >
            <v-list-item-icon>
              <span
                v-if="item.trackingLabel === 'News' && unreadNewsCount > 0"
                class="unread-messages-count"
              >
                {{ unreadNewsCount }}
              </span>
              <v-icon>{{ item.icon }}</v-icon>
            </v-list-item-icon>

            <v-badge
              v-if="item.badge && item.badge.condition({ showRegeneration })"
              :color="item.badge.color"
              :icon="item.badge.icon"
            >
              <v-list-item-content>
                <v-list-item-title>{{ $t(item.label) }}</v-list-item-title>
              </v-list-item-content>
            </v-badge>
            <v-list-item-content v-else>
              <v-list-item-title>{{ $t(item.label) }}</v-list-item-title>
            </v-list-item-content>

            <v-list-item-action v-if="item.href">
              <v-icon
                color="grey lighten-1"
                size="14"
              >
                mdi-open-in-new
              </v-icon>
            </v-list-item-action>
          </v-list-item>
        </v-list>
      </div>

      <!-- User Area -->
      <template v-slot:append>
        <FeatureFlag
          :feature-name="featureNames.USAGE_COUNTER"
        >
          <template v-slot="props">
            <v-list-item
              v-if="isDrawerOpen && company"
              class="px-0"
              @mounted="isUsageCounterActive = true"
            >
              <v-list-item-content>
                <UsageCounter
                  :product-type="productType"
                  v-bind="props"
                />
              </v-list-item-content>
            </v-list-item>
          </template>
          <template
            #fallback
            v-slot:append
          >
            <!-- User Area -->
            <v-btn
              v-if="isEntryPlan"
              block
              color="primary"
              class="my-4"
              :to="{ name: 'Settings', params: { section: 'contract' }, query: { showUpgrade: '1' } }"
              @click="$tracking.event('Sidebar', 'Clicked', 'Upgrade')"
            >
              <v-icon class="mr-2">
                mdi-one-up
              </v-icon>
              <v-expand-x-transition>
                <div v-if="isDrawerOpen">
                  {{ $t('navigation.labels.upgrade') }}
                </div>
              </v-expand-x-transition>
            </v-btn>

            <v-menu
              v-model="isUserMenuOpen"
              style="z-index:11"
              offset-y
              top
              @mounted="isUsageCounterActive = false"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-list-item
                  dense
                  v-bind="attrs"
                  class="rounded-lg user-item"
                  :class="{'grey lighten-3': isDrawerOpen}"
                  v-on="on"
                  @click="trackClick('User Menu')"
                >
                  <v-list-item-avatar>
                    <PackageLogo
                      :current-package="productType"
                      :show-label="false"
                      class="mr-6"
                      :style="{'marginTop':'-38px', marginLeft: '25px'}"
                      size="medium"
                    />
                  </v-list-item-avatar>

                  <v-list-item-content>
                    <div class="d-flex justify-space-between">
                      <div>
                        <span
                          v-if="$auth.user.given_name && $auth.user.family_name"
                          class="text-caption font-weight-bold"
                        > {{ $auth.user.given_name + ' ' + $auth.user.family_name }}</span>
                        <span
                          v-else
                          class="text-caption font-weight-bold d-inline-block text-truncate"
                          style="max-width: 140px"
                        > {{ $auth.user.email }}</span>
                        <br><span
                          v-if="company"
                          class="text-caption font-weight-bold grey--text d-inline-block text-truncate"
                          style="max-width: 140px"
                        > {{ company.name }}</span>
                      </div>
                      <v-icon
                        small
                        class="pl-2"
                      >
                        {{ isUserMenuOpen ? "mdi-arrow-down" : "mdi-arrow-up" }}
                      </v-icon>
                    </div>
                  </v-list-item-content>
                </v-list-item>
              </template>
              <v-list>
                <v-list-item
                  to="/settings/profile"
                  dense
                  exact
                  link
                  @click="trackClick('Profile')"
                >
                  <v-list-item-icon><v-icon>mdi-account</v-icon></v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>{{ $t('navigation.labels.profile') }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item
                  dense
                  @click="logout"
                >
                  <v-list-item-icon><v-icon>mdi-logout</v-icon></v-list-item-icon>
                  <v-list-item-title>{{ $t('navigation.labels.logout') }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </template>
        </FeatureFlag>
      </template>
    </v-navigation-drawer>
  </div>
</template>

<script>
import COMPANY_BASE from '../../queries/CompanyBase.gql'
import PENDING_CHANGES_FOR_REGENERATION from '../../queries/PendingChangesForRegeneration.gql'
import featureNames from '@/lib/featureNames'
import brandingMixin from '@/mixins/branding'
import featureMixin from '@/mixins/feature'
import PackageLogo from '@/components/PackageLogo.vue'
import { productFruits } from 'product-fruits'
import UsageCounter from '@/components/UsageCounter'
import { ProductType as ProductTypes } from '@/lib/productTypes'
import FeatureFlag from '@/components/FeatureFlag.vue'

const BASIC_NAVIGATION = [
  {
    label: 'navigation.labels.dashboard',
    trackingLabel: 'Dashboard',
    to: '/dashboard',
    icon: 'mdi-view-dashboard-outline'
  },
  {
    label: 'navigation.labels.topics',
    trackingLabel: 'Topics',
    to: '/topics',
    icon: 'mdi-database',
    feature: featureNames.TOPIC,
    checkConfigIsActive: (config) => config?.isUserManageable ?? false
  },
  {
    label: 'navigation.labels.leads',
    trackingLabel: 'Leads',
    to: '/leads',
    icon: 'mdi-account-multiple-outline',
    feature: featureNames.LEAD,
    permission: 'lead:read'
  },
  {
    label: 'navigation.labels.landingpages',
    trackingLabel: 'Landingpages',
    to: '/landingpages',
    icon: 'mdi-button-pointer',
    feature: featureNames.LANDINGPAGE,
    permission: 'material:read'
  },
  {
    label: 'navigation.labels.content-creator',
    trackingLabel: 'Content Creator',
    to: '/content-creator',
    icon: 'mdi-brain',
    feature: featureNames.CONTENT_CREATOR,
    permission: 'ai:read'
  },
  {
    label: 'navigation.labels.valuation',
    trackingLabel: 'Valuation',
    to: '/valuation',
    icon: 'mdi-file-chart-check-outline',
    feature: featureNames.VALUATION,
    checkConfigIsActive: (config) => config.isInAppValuationEnabled,
    permission: 'valuation:read'
  },
  {
    label: 'navigation.labels.partner',
    trackingLabel: 'Partner',
    to: '/partner',
    icon: 'mdi-handshake-outline',
    feature: featureNames.PARTNER,
    permission: 'partner:read'
  },
  {
    label: 'navigation.labels.performance',
    trackingLabel: 'Performance Report',
    to: '/performance',
    icon: 'mdi-gauge',
    feature: featureNames.PERFORMANCE_MARKETING,
    permission: 'performance:read'
  },
  {
    label: 'navigation.labels.statistic-dashboard',
    trackingLabel: 'Statistic Dashboard',
    to: '/statistic-dashboard',
    icon: 'mdi-poll',
    feature: featureNames.STATISTIC_DASHBOARD,
    permission: 'lead:read'
  },
  {
    label: 'navigation.labels.settings',
    trackingLabel: 'Settings',
    to: '/settings',
    icon: 'mdi-cog-outline',
    permission: 'settings:read'
  }
]

const ONBOARING_NAVIGATION = [{
  label: 'navigation.labels.onboarding',
  trackingLabel: 'Onboarding',
  to: '/onboarding',
  icon: 'mdi-view-dashboard-outline'
}]

export default {
  components: {
    PackageLogo,
    UsageCounter,
    FeatureFlag
  },
  mixins: [brandingMixin, featureMixin],
  inject: ['role'],
  props: {
    isDrawerCollapsible: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      // Will be initially set to boolean by the drawer component
      // depending on the viewport size
      showDrawer: null,
      isDrawerOpen: !this.isDrawerCollapsible,
      pendingChangesForRegeneration: {},
      featureNames,
      isUserMenuOpen: false,
      unreadNewsCount: 0,
      ProductTypes,
      isUsageCounterActive: false
    }
  },
  computed: {
    isMobile () {
      return this.$vuetify.breakpoint.smAndDown
    },
    languageCode () {
      return this.$i18n.locale
    },
    productType () {
      return this.company?.productType
    },
    isEntryPlan () {
      const entryPlans = [ProductTypes.BEGINNER, ProductTypes.LIGHT, ProductTypes.PIONEER]
      return this.productType && entryPlans.includes(this.productType)
    },
    showIsNew () {
      return !this.isPlatformMarkero
    },
    showRegeneration () {
      return this.isFeatureActive(this.featureNames.REGENERATION) &&
      Object.keys(this.pendingChangesForRegeneration).length > 0
    },
    inOnboarding () {
      return this.company?.state.stringValues.includes('ONBOARDING')
    },
    isInactive () {
      return this.company?.state.stringValues.includes('CREATED') ||
        this.company?.state.stringValues.includes('INACTIVE')
    },
    navItems () {
      if (!this.company || this.isInactive) {
        return []
      }

      if (this.inOnboarding) {
        return this.getActiveNavItems(ONBOARING_NAVIGATION)
      }

      return this.getActiveNavItems(BASIC_NAVIGATION)
    },
    navItemsBottimmo () {
      if (!this.company) {
        return []
      }
      return this.inOnboarding || this.isInactive
        ? this.getActiveNavItems(this.getNavItemsBottimmo().filter(({ onboarding }) => onboarding))
        : this.getActiveNavItems(this.getNavItemsBottimmo())
    },
    filteredNavItemsBottimmo () {
      return this.navItemsBottimmo.filter(({ trackingLabel }) => {
        if (trackingLabel === 'News') {
          return !this.$vuetify.breakpoint.mdAndDown
        }
        return true
      })
    },
    news () {
      return this.navItemsBottimmo.find(({ trackingLabel }) => trackingLabel === 'News')
    }
  },
  watch: {
    showDrawer (val) {
      if (val) {
        productFruits.safeExec(() => {
          this.setupNewsList()
        })
      }
    }
  },
  mounted () {
    productFruits.safeExec(() => {
      this.setupNewsList()
    })
  },
  apollo: {
    company: {
      query: COMPANY_BASE,
      variables () {
        return {
          id: this.$auth.user.companyId
        }
      },
      skip () {
        return this.$auth.loading || !this.$auth.isAuthenticated
      }
    },
    pendingChangesForRegeneration: {
      query: PENDING_CHANGES_FOR_REGENERATION,
      variables () {
        return { companyId: this.company.id }
      },
      skip () {
        return !this.company || !this.role.isAuthorizedFor('settings-company:read')
      }
    }
  },
  methods: {
    setupNewsList () {
      const el = document.getElementById('news')
      if (!el) {
        return
      }
      try {
        // eslint-disable-next-line
        window.productFruits.api.announcementsV2.attachNewsWidgetToElement(el);

        // eslint-disable-next-line
        window.productFruits.api.announcementsV2.listen('newsfeed-unread-count-changed', (data) => {
          this.unreadNewsCount = data.count
        })
      } catch (e) {
        // eslint-disable-next-line
        console.error('Could not load news list', e)
      }
    },
    // The name should be changed, the nav is used for both domains
    getNavItemsBottimmo () {
      const navItems = []

      if (this.isPlatformMarkero) {
        const news = this.createNewsNavItem()
        navItems.push(news)
      }

      const support = this.createSupportNavItem()
      navItems.push(support)

      const legalNavItem = {
        label: 'navigation.labels.legal',
        trackingLabel: 'Legal',
        to: '/legal',
        onboarding: true,
        icon: 'mdi-file-document-outline',
        permission: 'legal:read'
      }
      navItems.push(legalNavItem)

      return navItems
    },
    createNewsNavItem () {
      return {
        label: 'navigation.labels.news',
        trackingLabel: 'News',
        icon: 'mdi-bell-outline',
        onboarding: false
      }
    },
    createSupportNavItem () {
      const support = {
        label: 'navigation.labels.support',
        trackingLabel: 'HelpCenter',
        icon: 'mdi-lifebuoy',
        onboarding: false,
        permission: 'support:read'
      }

      if (this.isPlatformMarkero && process.env.VUE_APP_PRODUCT_FRUITS_HELP_CENTER) {
        support.href = (new URL(this.languageCode, process.env.VUE_APP_PRODUCT_FRUITS_HELP_CENTER)).toString()
        support.target = '_blank'
      } else {
        support.to = '/support'
      }

      return support
    },
    getActiveNavItems (items) {
      return items.filter(({ feature: featureSlug, checkConfigIsActive, permission }) => {
        const feature = featureSlug ? this.$features.feature(featureSlug) : null
        if (feature && (!feature.isActive || (checkConfigIsActive && !checkConfigIsActive(feature.config)))) {
          return false
        }

        if (permission && !this.role.isAuthorizedFor(permission)) {
          return false
        }

        return true
      })
    },
    logout () {
      this.trackClick('Logout')
      this.$auth.logout()
    },
    goHome () {
      this.$router.push({
        name: this.$auth.isAuthenticated ? 'dashboard' : 'home'
      }).catch((err) => {
        if (err.name !== 'NavigationDuplicated') {
          throw err
        }
      })
    },
    handleItemClick (label) {
      if (this.isMobile) { this.showDrawer = false }
      this.trackClick(label)
    },
    trackClick (label) {
      this.$tracking.event('Sidebar', 'Clicked', label)
    },
    onUsageCounterFeatureChange (event) {
      this.isUsageCounterActive = event.enabled
    },
    updateDrawerState () {
      this.$nextTick(() => {
        const drawerEl = this.$refs.drawer.$el
        const className = 'v-navigation-drawer--mini-variant'
        if (!drawerEl || !drawerEl.classList.contains(className) === this.isDrawerOpen) {
          return
        }
        this.isDrawerOpen = !drawerEl.classList.contains(className)
      })
    }
  }
}
</script>

<style scoped>
.v-navigation-drawer--mini-variant .v-list-item > *:first-child{
  margin-right:0px !important;
}

.clickable {
  cursor: pointer;
}

.group-header{
  width: 100%;
}

.group-header :deep(.v-list-group__header) {
  padding: 0;
}

.group-item {
  min-height: 12px;
}

.navbar .v-list-item--active {
  border-radius: 10px;
}

.navbar .v-list-item--active:before {
 opacity: 0;
}

.navbar .v-list-item:before {
 border-radius: 10px;
}

.navbar{
  z-index: 10;
}

@media (max-width: 1260px) {
  /*
  Bigger z-index then product fruits containers
  */
  .navbar{
    z-index: unset;
  }
  .v-navigation-drawer--open, .v-menu__content{
    z-index: 10002992 !important;
  }

  .navigation-drawer-height {
    height: calc(100% - env(safe-area-inset-bottom, 0px)) !important;
    box-sizing: border-box !important;
  }
}

.user-item{
  transition: background-color .4s;
}

.unread-messages-count {
    width: 15px;
    height: 15px;
    position: absolute;
    top: -1px;
    background-color: red;
    color: white;
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 10px;
    font-weight: bold;
    z-index: 9;
}

.user-section {
  position: relative;
  cursor: pointer;
  display: flex;
  align-items: center;
  width: 100%;
}

.user-section .truncate {
  max-width: calc(100% - 66px);
  min-width: 0;
}

.user-section .v-list-item-content > div {
  min-width: 0;
}

.user-details {
  min-width: 0;
  flex: 1;
  margin-right: 8px;
}

.truncate {
  display: block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.menu-button {
  pointer-events: none;
  opacity: 0.6;
  transition: opacity 0.2s, background-color 0.2s;
  position: absolute;
  right: 8px;
}

.menu-button::before {
  display: none;
}

.user-section:hover .menu-button {
  opacity: 1;
  background-color: rgba(0, 0, 0, 0.04);
}

.portrait {
  border-radius: 100%;
  object-fit: cover;
}

.navigation-drawer-height ::v-deep(.v-navigation-drawer__content) {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.navigation-drawer-height ::v-deep(.v-list:last-child),
.navigation-drawer-height ::v-deep(.v-navigation-drawer__append) {
  margin-top: auto;
  margin-bottom: 0;
}

.upgrade-button {
  text-transform: unset;
  font-weight: 700;
  padding: 0 8px !important;
  margin-right: 8px;
}

.news-icon {
  margin: 8px 0 !important;
  margin-bottom: 0;
  top: 4px;
  position: relative;
}

.news-icon .unread-messages-count {
  top: -4px;
}

::v-deep(.v-app-bar__nav-icon .v-icon) {
  font-size: 30px !important;
  width: 30px !important;
  height: 30px !important;
}
.v-application--is-ltr .v-list-item__icon:first-child {
  margin-right: 16px;
}

.navigation-container {
  display: flex;
  flex-direction: column;
  height: v-bind("isUsageCounterActive ? 'calc(100% - 50px)' : '100%'");

}

.primary-navigation {
  flex: 1;
  overflow-y: auto !important;
  overflow-x: hidden;
}

.bottom-navigation {
  margin-top: 0;
  min-height: fit-content;
}

.navigation-drawer-height ::v-deep(.v-navigation-drawer__content) {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

</style>
